import {
  isPathInventoryConsumedStatus,
  parseLabTestBomEntries,
  buildLabTestLookupMaps,
  resolveLabTestForUsage,
} from "./pathInventorySummary";

const firstDefined = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return undefined;
};

const isUsableDoctorName = (value) => {
  if (value == null || value === "") return false;
  const s = String(value).trim();
  if (!s) return false;
  const lower = s.toLowerCase();
  return (
    lower !== "n/a" &&
    lower !== "na" &&
    lower !== "null" &&
    lower !== "undefined" &&
    lower !== "—" &&
    lower !== "-" &&
    lower !== "unknown doctor" &&
    lower !== "unknown" &&
    !lower.startsWith("dr. id:")
  );
};

const pickUsableDoctorName = (...values) => {
  for (const value of values) {
    if (typeof value === "object" && value && !Array.isArray(value)) {
      const nested = pickUsableDoctorName(
        value.name,
        value.doctorName,
        value.fullName,
        [value.firstName, value.lastName].filter(Boolean).join(" ")
      );
      if (nested) return nested;
      continue;
    }
    if (isUsableDoctorName(value)) return String(value).trim();
  }
  return "";
};

const toNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

export const resolveSupplierDisplayName = (value) => {
  if (value == null || value === "") return "";
  if (typeof value === "string" || typeof value === "number") {
    return String(value).trim();
  }
  if (typeof value === "object") {
    return String(
      value.name ||
        value.supplierName ||
        value.fromName ||
        value.vendorName ||
        value.label ||
        ""
    ).trim();
  }
  return "";
};

export const getPathologyTransactionItemName = (transaction) =>
  String(
    firstDefined(
      transaction?.itemName,
      transaction?.item_name,
      transaction?.pathItemName,
      transaction?.inventoryName,
      transaction?.name,
      transaction?.medicineName
    ) || ""
  ).trim();

export const getPathologyTransactionInvoiceId = (transaction) =>
  firstDefined(
    transaction?.invoiceId,
    transaction?.invoice_id,
    transaction?.invoice?.id,
    transaction?.billId,
    transaction?.bill_id
  ) ?? null;

const unwrapInvoiceRows = (invoices) => {
  if (Array.isArray(invoices)) return invoices;
  if (Array.isArray(invoices?.data)) return invoices.data;
  if (Array.isArray(invoices?.content)) return invoices.content;
  return [];
};

export const isPathologyTestInvoice = (invoice) => {
  if (!invoice || typeof invoice !== "object") return false;
  const type = String(invoice.invoiceType || invoice.invoice_type || "").toUpperCase();
  const particulars = String(invoice.particulars || "").toUpperCase();
  return type.includes("PATHOLOGYTEST") || particulars.includes("PATHOLOGYTEST");
};

export const findPathologyTestInvoice = (
  invoices = [],
  { prescriptionId, patientId, invoiceId } = {}
) => {
  const rows = unwrapInvoiceRows(invoices).filter(isPathologyTestInvoice);
  if (invoiceId != null && invoiceId !== "") {
    const byId = rows.find((inv) => String(inv?.id) === String(invoiceId));
    if (byId) return byId;
  }
  if (prescriptionId != null && prescriptionId !== "") {
    const byPrescription = rows.find((inv) => {
      const linked =
        inv?.prescriptionId ??
        inv?.prescription_id ??
        inv?.prescription?.id ??
        null;
      return linked != null && String(linked) === String(prescriptionId);
    });
    if (byPrescription) return byPrescription;
  }
  if (patientId != null && patientId !== "") {
    const byPatient = rows.find((inv) => {
      const linked = inv?.patientId ?? inv?.patient_id ?? inv?.patient?.id ?? null;
      return linked != null && String(linked) === String(patientId);
    });
    if (byPatient) return byPatient;
  }
  return null;
};

export const findPathologyTestInvoiceId = (invoices, keys) => {
  const invoice = findPathologyTestInvoice(invoices, keys);
  return invoice?.id ?? null;
};

export const getPathologyTransactionLabTestId = (transaction) =>
  firstDefined(
    transaction?.labTestId,
    transaction?.lab_test_id,
    transaction?.testId,
    transaction?.test_id,
    transaction?.labTest?.id
  ) ?? null;

export const getPathologyTransactionPatientId = (transaction) =>
  firstDefined(
    transaction?.patientId,
    transaction?.patient_id,
    transaction?.patient?.id
  ) ?? null;

export const getPathologyTransactionPrescriptionId = (transaction) =>
  firstDefined(
    transaction?.prescriptionId,
    transaction?.prescription_id,
    transaction?.opdPrescriptionId,
    transaction?.opd_prescription_id,
    transaction?.ipdPrescriptionId,
    transaction?.ipd_prescription_id,
    transaction?.prescription?.id
  ) ?? null;

export const getPathologyTransactionDoctorName = (transaction) =>
  pickUsableDoctorName(
    transaction?.doctorName,
    transaction?.doctor_name,
    transaction?.assignedByName,
    transaction?.resolvedDoctorName,
    transaction?.doctor
  );

export const getPathologyTransactionSupplierName = (transaction) =>
  resolveSupplierDisplayName(transaction?.supplierName) ||
  resolveSupplierDisplayName(transaction?.supplier) ||
  resolveSupplierDisplayName(transaction?.fromName) ||
  resolveSupplierDisplayName(transaction?.vendorName) ||
  "";

export const getPathologyTransactionQuantity = (transaction) => {
  const qty = toNumber(
    firstDefined(
      transaction?.deductedQuantity,
      transaction?.deducted_quantity,
      transaction?.quantity,
      transaction?.addedQuantity
    ),
    0
  );
  return Math.abs(qty);
};

const unwrapList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.content)) return data.content;
  return [];
};

const collectNestedLabTests = (prescription) => {
  const nested = [
    prescription?.patientLabTests,
    prescription?.labTests,
    prescription?.opdLabTests,
    prescription?.tests,
  ].find((list) => Array.isArray(list) && list.length > 0);
  return Array.isArray(nested) ? nested : [];
};

export const flattenOpdLabReports = (prescriptions = []) => {
  const reports = [];
  unwrapList(prescriptions).forEach((rx) => {
    const rxPatientName = firstDefined(
      rx?.patientName,
      rx?.patient_name,
      rx?.name,
      rx?.patient?.name,
      rx?.patient?.patientName
    );
    const rxDoctorName = pickUsableDoctorName(
      rx?.doctorName,
      rx?.doctor_name,
      rx?.assignedByName,
      rx?.resolvedDoctorName,
      rx?.doctor
    );
    const rxPatientId = firstDefined(rx?.patientId, rx?.patient_id, rx?.patient?.id);

    const nested = collectNestedLabTests(rx);
    if (nested.length > 0) {
      nested.forEach((test) => {
        reports.push({
          ...test,
          patientId: firstDefined(test?.patientId, test?.patient_id, rxPatientId),
          patientName: firstDefined(
            test?.patientName,
            test?.patient_name,
            rxPatientName
          ),
          doctorName:
            pickUsableDoctorName(
              test?.doctorName,
              test?.doctor_name,
              test?.assignedByName,
              test?.resolvedDoctorName,
              test?.doctor
            ) || rxDoctorName,
          prescriptionId: firstDefined(test?.prescriptionId, rx?.id, rx?.prescriptionId),
          invoiceId: firstDefined(test?.invoiceId, test?.invoice_id, rx?.invoiceId, rx?.invoice_id),
          doctorId: firstDefined(
            test?.doctorId,
            test?.doctor_id,
            rx?.doctorId,
            rx?.doctor_id,
            rx?.doctor?.id
          ),
          patientType: "opd",
        });
      });
      return;
    }
    // flat row (individual lab test record)
    if (firstDefined(rx?.labTestId, rx?.testId, rx?.testName, rx?.resultStatus)) {
      reports.push({
        ...rx,
        patientName: firstDefined(rx?.patientName, rx?.patient_name, rx?.name, rx?.patient?.name),
        doctorName: pickUsableDoctorName(
          rx?.doctorName,
          rx?.doctor_name,
          rx?.assignedByName,
          rx?.resolvedDoctorName,
          rx?.doctor
        ),
        doctorId: firstDefined(rx?.doctorId, rx?.doctor_id, rx?.doctor?.id),
        patientType: rx?.patientType || "opd",
      });
    }
  });
  return reports;
};

const resolveTransactionDate = (...values) => {
  for (const value of values) {
    if (value == null || value === "") continue;
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return parsed.toISOString();
  }
  return null;
};

const bomUnitType = (bomMap, bomId) => {
  const bom = bomMap.get(Number(bomId));
  return bom?.unitType || bom?.unit_type || bom?.qtyUnit || "";
};

export const buildDerivedPathologyInTransactions = ({
  inventoryGroups = [],
  invoices = [],
  boms = [],
} = {}) => {
  const invoiceList = unwrapList(invoices);
  const invoiceById = new Map(
    invoiceList
      .filter((inv) => inv?.id != null)
      .map((inv) => [String(inv.id), inv])
  );
  const bomMap = new Map(
    (Array.isArray(boms) ? boms : []).map((bom) => [Number(bom.id), bom])
  );
  const transactions = [];

  (Array.isArray(inventoryGroups) ? inventoryGroups : []).forEach((group) => {
    (group?.records || []).forEach((record) => {
      const qty = toNumber(record?.quantity ?? record?.qty, 0);
      if (qty <= 0) return;
      const invoiceId = firstDefined(record?.invoiceId, record?.invoice_id);
      const invoice = invoiceId != null ? invoiceById.get(String(invoiceId)) : null;
      const supplierName =
        resolveSupplierDisplayName(record?.supplierName) ||
        resolveSupplierDisplayName(record?.fromName) ||
        resolveSupplierDisplayName(invoice?.supplierName) ||
        resolveSupplierDisplayName(invoice?.fromName) ||
        "";
      const linePrice = toNumber(record?.price, 0);
      const costPerUnit = qty > 0 ? linePrice / qty : linePrice;
      const bomId = firstDefined(record?.bomId, record?.bom_id, group?.bomId);

      transactions.push({
        id: `derived-in-${record?.id ?? `${group?.name}-${record?.batchNumber}`}`,
        derived: true,
        inventoryId: record?.id ?? null,
        bomId,
        itemName: group?.name || record?.name || "",
        batchNumber: record?.batchNumber || record?.batch_number || "",
        unitType: bomUnitType(bomMap, bomId) || record?.unitType || "",
        deductedQuantity: qty,
        transactionType: "IN",
        transactionDate: resolveTransactionDate(
          record?.createdAt,
          record?.created_at,
          record?.createdDate,
          invoice?.date,
          invoice?.invoiceDate,
          invoice?.createdAt
        ),
        supplierName,
        supplierId: firstDefined(record?.supplierId, invoice?.supplierId),
        invoiceId: invoiceId ?? null,
        costPerUnit,
        expiryDate: firstDefined(
          record?.expirationDate,
          record?.expiryDate,
          record?.expiry
        ),
      });
    });
  });

  return transactions;
};

export const buildDerivedPathologyOutTransactions = ({
  opdReports = [],
  ipdLabTests = [],
  labTests = [],
  boms = [],
} = {}) => {
  const bomsById = new Map(
    (Array.isArray(boms) ? boms : []).map((bom) => [Number(bom.id), bom])
  );
  const { byId: labTestsById, byName: labTestsByName } =
    buildLabTestLookupMaps(labTests);
  const transactions = [];
  const seenOpd = new Set();
  const seenIpd = new Set();

  flattenOpdLabReports(opdReports).forEach((report) => {
    if (!isPathInventoryConsumedStatus(report?.resultStatus ?? report?.status)) return;

    const reportKey = firstDefined(report?.reportId, report?.id, report?.labTestId);
    const dedupeKey = `opd-${reportKey}-${report?.labTestId || report?.testName || ""}`;
    if (seenOpd.has(dedupeKey)) return;
    seenOpd.add(dedupeKey);

    const labTest = resolveLabTestForUsage({
      labTestId: report?.labTestId ?? report?.testId,
      testName: report?.testName,
      labTestsById,
      labTestsByName,
    });
    if (!labTest) return;

    parseLabTestBomEntries(labTest, bomsById).forEach((entry) => {
      transactions.push({
        id: `derived-out-opd-${dedupeKey}-${entry.bomId}`,
        derived: true,
        bomId: entry.bomId,
        itemName: entry.itemName,
        unitType: bomUnitType(bomsById, entry.bomId),
        deductedQuantity: entry.requiredQuantity,
        transactionType: "OUT",
        transactionDate: resolveTransactionDate(
          report?.completedAt,
          report?.completed_at,
          report?.resultDate,
          report?.updatedAt,
          report?.createdAt,
          report?.date
        ),
        patientId: firstDefined(report?.patientId, report?.patient_id),
        patientName: firstDefined(report?.patientName, report?.patient_name),
        patientType: "opd",
        doctorName: pickUsableDoctorName(
          report?.doctorName,
          report?.doctor_name,
          report?.assignedByName,
          report?.resolvedDoctorName,
          report?.doctor
        ),
        doctorId: firstDefined(report?.doctorId, report?.doctor_id),
        invoiceId: firstDefined(report?.invoiceId, report?.invoice_id),
        prescriptionId: firstDefined(report?.prescriptionId, report?.prescription_id),
        labTestId: labTest.id,
        labTestName: report?.testName || labTest?.name || labTest?.testName,
      });
    });
  });

  unwrapList(ipdLabTests).forEach((test) => {
    const status = test?.testStatus || test?.resultStatus || test?.status;
    if (!isPathInventoryConsumedStatus(status)) return;

    const testKey = firstDefined(test?.id, test?.labTestId, test?.testId);
    if (testKey != null) {
      if (seenIpd.has(String(testKey))) return;
      seenIpd.add(String(testKey));
    }

    const labTest = resolveLabTestForUsage({
      labTestId: test?.testId ?? test?.labTestId,
      testName: test?.testName || test?.resolvedTestName,
      labTestsById,
      labTestsByName,
    });
    if (!labTest) return;

    parseLabTestBomEntries(labTest, bomsById).forEach((entry) => {
      transactions.push({
        id: `derived-out-ipd-${testKey}-${entry.bomId}`,
        derived: true,
        bomId: entry.bomId,
        itemName: entry.itemName,
        unitType: bomUnitType(bomsById, entry.bomId),
        deductedQuantity: entry.requiredQuantity,
        transactionType: "OUT",
        transactionDate: resolveTransactionDate(
          test?.completedAt,
          test?.resultDate,
          test?.updatedAt,
          test?.createdAt,
          test?.date
        ),
        patientId: firstDefined(test?.patientId, test?.patient_id),
        patientName: firstDefined(
          test?.patientName,
          test?.patient_name,
          test?.patient?.name,
          test?.patient?.patientName
        ),
        patientType: "ipd",
        doctorName: pickUsableDoctorName(
          test?.doctorName,
          test?.doctor_name,
          test?.assignedByName,
          test?.resolvedDoctorName,
          test?.doctor
        ),
        doctorId: firstDefined(
          test?.doctorId,
          test?.doctor_id,
          test?.assignedBy,
          test?.assigned_by
        ),
        invoiceId: firstDefined(test?.invoiceId, test?.invoice_id),
        prescriptionId: firstDefined(
          test?.prescriptionId,
          test?.ipdPrescriptionId,
          test?.ipd_prescription_id
        ),
        admissionId: firstDefined(test?.admissionId, test?.admission_id),
        labTestId: labTest.id,
        labTestName: test?.testName || test?.resolvedTestName || labTest?.name,
      });
    });
  });

  return transactions;
};

export const buildDerivedPathologyTransactions = (args) => {
  const inbound = buildDerivedPathologyInTransactions(args);
  const outbound = buildDerivedPathologyOutTransactions(args);
  return [...inbound, ...outbound].sort(
    (a, b) => new Date(b.transactionDate || 0) - new Date(a.transactionDate || 0)
  );
};
